import { gql } from '@apollo/client';
import {
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Link,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useCallback } from 'react';
import { ApiKeysActionTable } from '~components/organization/ApiKeysActionTable';
import { CreateApiKeyModal } from '~components/organization/CreateApiKeyModal';
import { InfoAlert } from '~components/ui/Alert';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import { useAnalyticsReporter } from '~utils/analytics';
import { useFeatureFlag } from '~utils/features';
import { useGetApiKeysQuery } from './__generated__/ApiKeysSettings.graphql';

export function ApiKeysSettings() {
  const createApiKeyModal = useDisclosure();
  const { data, loading, error, refetch } = useGetApiKeysQuery();
  const analytics = useAnalyticsReporter();

  // Remove once subscriptions are released to everyone: https://inthepocket.atlassian.net/browse/TPVWAVE-1460
  const { isEnabled: isSubscriptionsEnabled } = useFeatureFlag('subscriptions');

  const handleCreateApiKey = useCallback(() => {
    analytics.track('apiKeyCreateStart');

    createApiKeyModal.onOpen();
  }, [analytics, createApiKeyModal]);

  const handleCreateApiKeySuccess = useCallback(() => {
    analytics.track('apiKeyCreateComplete');

    refetch();
  }, [analytics, refetch]);

  const documentationLink = (
    <Link
      color="blue.500"
      href="https://docs.wave.ppds.com/"
      target="_blank"
      rel="noopener noreferrer"
    >
      {t('documentation')}
    </Link>
  );

  return (
    <VerticalTabContent title={t('APIKeys')} isLoading={loading} error={error} data={data}>
      {({ me: { apiKeys } }) => (
        <>
          <Stack direction="row" spacing="20" mb="6">
            <Box flex="1">
              <Text fontSize="md" color="gray.500" maxWidth="700">
                {t('documentationMsg')} {documentationLink}.
              </Text>
            </Box>
            <Box>
              <Button variant="solid" colorScheme="blue" onClick={handleCreateApiKey}>
                {t('generateNewKey')}
              </Button>
            </Box>
          </Stack>
          {
            // Remove once subscriptions are released to everyone: https://inthepocket.atlassian.net/browse/TPVWAVE-1460
          }
          {isSubscriptionsEnabled && (
            <InfoAlert mt="6" mb="10">
              <AlertTitle>{t('apiKeyAlert')}</AlertTitle>
              <AlertDescription>{t('apiKeyAlertDesc')}</AlertDescription>
            </InfoAlert>
          )}
          {!isEmpty(apiKeys) ? (
            <ApiKeysActionTable apiKeys={apiKeys} />
          ) : (
            <Text color="gray.500">{t('APIKeysNotFound')}</Text>
          )}
          <CreateApiKeyModal
            isOpen={createApiKeyModal.isOpen}
            onCancel={createApiKeyModal.onClose}
            onSuccess={handleCreateApiKeySuccess}
          />
        </>
      )}
    </VerticalTabContent>
  );
}

ApiKeysSettings.graphql = {
  queries: {
    ApiKeys: gql`
      query GetApiKeys {
        me {
          apiKeys {
            ...ApiKeysActionTable_apiKey
          }
        }
      }
    `,
  },
};
